// Client for the outcome-description checks in techladder-core (POST /agents/outcomes/validate) and
// helpers to read the findings that a blocked save returns. Everything here fails soft: if the check
// itself cannot run, publishing carries on and core still enforces its own rules.
import { ApiError, core } from "../../lib/api";

export interface OutcomeFinding {
  rule_id: string;
  source: "rule" | "llm";
  severity: "block" | "warn";
  field_name: string;
  message: string;
  quote: string;
  suggestion: string;
  impact: "text only" | "changes stored values" | "breaks a reference";
  field_state: "new" | "legacy-edited" | "legacy-unchanged";
  existing: boolean;
  blocking: boolean;
  overridden: boolean;
  override_reason: string;
  accepted?: boolean; // accepted earlier with a reason, stored on the outcome: never blocks, not shown
}

export interface OutcomeValidation {
  mode: "off" | "shadow" | "enforce" | string;
  can_save: boolean;
  field_states: Record<string, string>;
  counts: Record<string, number>;
  llm: { enabled: boolean; ran: boolean; fields_reviewed: number; from_cache: number; error: string | null };
  findings: OutcomeFinding[];
}

export interface OutcomeOverride {
  field_name: string;
  rule_id: string;
  reason: string;
}

export interface ValidateRequest {
  agent_id?: string;
  workflow_id?: string;
  version_id?: string;
  call_outcomes: unknown[];
  tools?: unknown[];
  llm_review?: boolean;
  llm_include_existing?: boolean;
}

export async function validateOutcomes(request: ValidateRequest): Promise<OutcomeValidation | null> {
  try {
    return await core<OutcomeValidation>("/api/v1/agents/outcomes/validate", {
      method: "POST",
      body: JSON.stringify(request),
    });
  } catch {
    return null; // older backend, network trouble, or no access: never stop a publish because of this
  }
}

// A blocked save answers 422 with { message, can_save: false, findings: [...] }.
export function findingsFromError(error: unknown): OutcomeFinding[] | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null;
  const findings = (error.data as { findings?: unknown } | undefined)?.findings;
  return Array.isArray(findings) ? (findings as OutcomeFinding[]) : null;
}

export interface SplitFindings {
  blocking: OutcomeFinding[]; // new or edited fields: must be fixed, or accepted with a reason
  suggestions: OutcomeFinding[]; // advice for new or edited fields
  existing: OutcomeFinding[]; // old untouched fields: never block, hidden unless asked for
  accepted: OutcomeFinding[]; // accepted earlier with a reason: never shown as a problem again
}

export function splitFindings(findings: OutcomeFinding[]): SplitFindings {
  const out: SplitFindings = { blocking: [], suggestions: [], existing: [], accepted: [] };
  for (const finding of findings) {
    if (finding.blocking) out.blocking.push(finding);
    else if (finding.accepted || finding.overridden) out.accepted.push(finding);
    else if (finding.existing) out.existing.push(finding);
    else out.suggestions.push(finding);
  }
  return out;
}

export const findingKey = (finding: Pick<OutcomeFinding, "field_name" | "rule_id">) => `${finding.field_name}::${finding.rule_id}`;

export const MIN_REASON_LENGTH = 3;

export function buildOverrides(blocking: OutcomeFinding[], reasons: Record<string, string>): OutcomeOverride[] {
  return blocking.map((finding) => ({
    field_name: finding.field_name,
    rule_id: finding.rule_id,
    reason: (reasons[findingKey(finding)] || "").trim(),
  }));
}

export function allReasonsGiven(blocking: OutcomeFinding[], reasons: Record<string, string>): boolean {
  return blocking.every((finding) => (reasons[findingKey(finding)] || "").trim().length >= MIN_REASON_LENGTH);
}

export function groupByField(findings: OutcomeFinding[]): Array<[string, OutcomeFinding[]]> {
  const map = new Map<string, OutcomeFinding[]>();
  for (const finding of findings) map.set(finding.field_name, [...(map.get(finding.field_name) || []), finding]);
  return [...map.entries()];
}

// The old plain-text outcomes that could be switched to the four boxes (rule R15), by field name.
export function oldFormatFields(findings: OutcomeFinding[]): string[] {
  return [...new Set(findings.filter((f) => f.rule_id === "R15").map((f) => f.field_name))];
}
